import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Box, Button, Container, Grid, Paper, Typography, 
    InputBase, IconButton, Stack, Chip, CardActionArea, useTheme, useMediaQuery 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, faMapMarkerAlt, faStar, faCalendarAlt, 
    faArrowRight, faUtensils, faBed, faTicketAlt, faShoppingBag 
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from "framer-motion";
import { getHomeData } from '../../controllers/public/HomeController';

// --- MOCK DATA PARA EL DESCUBRIMIENTO ---
const mockCategories = [
    { id: 1, label: 'Gastronomía', icon: faUtensils, color: '#FF7043' },
    { id: 2, label: 'Eventos', icon: faTicketAlt, color: '#AB47BC' },
    { id: 3, label: 'Hospedaje', icon: faBed, color: '#42A5F5' },
    { id: 4, label: 'Tiendas', icon: faShoppingBag, color: '#66BB6A' },
    { id: 5, label: 'Servicios', icon: faMapMarkerAlt, color: '#78909C' }
];



export default function Home() {
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchQuery, setSearchQuery] = useState('');

    const [merchants,setMerchants] = useState([]);
    const [events,setEvents] = useState([]);

    const redirect = (route) => {
        history.push(route);
    };

    useEffect(()=>{
        handleGetHomeData();
    },[]);

    const handleSearch = (e) => {
        e.preventDefault();
        if(searchQuery.trim() !== '') {
            // Lógica para enviar al buscador general
            redirect(`/search?q=${searchQuery}`);
        }
    };

    const handleGetHomeData = async () => {
        const {active_city_id, featured_merchants, upcoming_events} = await getHomeData();
        await setMerchants(featured_merchants);
        await setEvents(upcoming_events);
        return;
        
    }

    // --- LÓGICA DEL HERO DINÁMICO ---
    const heroImages = [
        'https://anzteenamora.com/static/media/portadaATE.78c7a52f93ce6c292a1c.jpg', // Gastronomía
        'https://images.unsplash.com/photo-1540039155732-68ee23e15b51?q=80&w=1974&auto=format&fit=crop', // Eventos/Conciertos
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1974&auto=format&fit=crop', // Hospedaje/Hoteles
        'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1974&auto=format&fit=crop'  // Vida Nocturna
    ];

    const [currentBg, setCurrentBg] = useState(0);

    useEffect(() => {
        // Cambiamos la imagen cada 6 segundos para no estresar visualmente al usuario
        const interval = setInterval(() => {
            setCurrentBg((prev) => (prev + 1) % heroImages.length);
        }, 6000); 
        
        // Limpiamos el intervalo al desmontar (regla de oro en performance)
        return () => clearInterval(interval);
    }, []);

    return (
        <Box sx={{ overflowX: 'hidden', pb: 10, bgcolor: '#fdfdfd' }}>

            {/* ========================================================= */}
            {/* 1. HERO DE DESCUBRIMIENTO (Search-First + Dinámico)       */}
            {/* ========================================================= */}
            <Box sx={{ 
                width: '100%',
                height: { xs: '65vh', md: '75vh' }, 
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pt: { xs: 8, md: 10 },
                overflow: 'hidden', // Evita que el fondo sobresalga
                bgcolor: '#1a1a1a' // Color base de carga
            }}>
                {/* Capas de imágenes con Crossfade (Framer Motion) */}
                <AnimatePresence>
                    <motion.div
                        key={currentBg}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }} // Transición muy suave de 1.5s
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%), url('${heroImages[currentBg]}')`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            zIndex: 1
                        }}
                    />
                </AnimatePresence>

                <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 10 }}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        
                        <Typography variant="h2" fontWeight="900" sx={{ color: '#fff', fontSize: { xs: '2.5rem', md: '4.5rem' }, lineHeight: 1.1, mb: 2, letterSpacing: '-1px' }}>
                            Tu ciudad en la <br/> palma de tu mano
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: '400', mb: 5, fontSize: { xs: '1rem', md: '1.2rem' } }}>
                            Descubre los mejores eventos, restaurantes y apoya a tus comercios locales favoritos.
                        </Typography>

                        {/* Buscador Principal */}
                        <Paper 
                            component="form" 
                            onSubmit={handleSearch}
                            sx={{ 
                                p: '4px 8px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                width: '100%', 
                                borderRadius: 12,
                                boxShadow: '0px 12px 30px rgba(0,0,0,0.2)',
                                mb: 3
                            }}
                        >
                            <IconButton sx={{ p: { xs: 1.5, md: 2 }, color: 'text.secondary' }} aria-label="menu">
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                            </IconButton>
                            <InputBase
                                sx={{ ml: 1, flex: 1, fontSize: { xs: '1rem', md: '1.1rem' } }}
                                placeholder="¿Qué buscas hoy? Ej. Sushi, Hotel, Concierto..."
                                inputProps={{ 'aria-label': 'buscar en la ciudad' }}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Button 
                                type="submit" 
                                variant="contained" 
                                disableElevation
                                sx={{ 
                                    borderRadius: 10, 
                                    px: { xs: 2, md: 4 }, 
                                    py: { xs: 1, md: 1.5 },
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    fontSize: '1rem'
                                }}
                            >
                                {isMobile ? <FontAwesomeIcon icon={faSearch}/> : 'Buscar'}
                            </Button>
                        </Paper>

                        {/* Chips de Tendencias Rápidas */}
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', mr: 1 }}>
                                Tendencias:
                            </Typography>
                            {['🍔 Hamburguesas', '🎭 Teatro', '🍻 Bares', '🏨 Hoteles'].map((tag, idx) => (
                                <Chip 
                                    key={idx} 
                                    label={tag} 
                                    size="small" 
                                    onClick={() => setSearchQuery(tag.substring(3))} 
                                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }} 
                                />
                            ))}
                        </Stack>

                    </motion.div>
                </Container>
            </Box>
            
          

            {/* ========================================================= */}
            {/* 2. CATEGORÍAS RÁPIDAS (Carrusel Nativo CSS)               */}
            {/* ========================================================= */}
            <Container maxWidth="xl" sx={{ mt: { xs: -3, md: -5 }, position: 'relative', zIndex: 20 }}>
                <Paper elevation={isMobile ? 0 : 2} sx={{ p: { xs: 0, md: 2 }, borderRadius: 4, bgcolor: { xs: 'transparent', md: '#fff' } }}>
                    <Stack 
                        direction="row" 
                        spacing={3} 
                        sx={{ 
                            overflowX: 'auto', 
                            pb: 2, pt: 1, px: 2,
                            /* Ocultar barra de scroll nativa */
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none'
                        }}
                    >
                        {mockCategories.map((cat) => (
                            <Box 
                                key={cat.id} 
                                onClick={() =>{
                                    if(cat.label === 'Eventos')
                                        return redirect(`/eventos`)
                                    else
                                        redirect(`/search/${cat.label}`)
                                } }
                                sx={{ 
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', 
                                    minWidth: { xs: '80px', md: '100px' }, cursor: 'pointer',
                                    group: 'true', transition: 'transform 0.2s',
                                    '&:hover': { transform: 'translateY(-5px)' }
                                }}
                            >
                                <Box sx={{ 
                                    width: 60, height: 60, borderRadius: '50%', 
                                    bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)', mb: 1,
                                    border: `1px solid #eaeaea`
                                }}>
                                    <FontAwesomeIcon icon={cat.icon} size="lg" color={cat.color} />
                                </Box>
                                <Typography variant="caption" fontWeight="bold" color="text.primary">{cat.label}</Typography>
                            </Box>
                        ))}
                    </Stack>
                </Paper>
            </Container>

            {/* ========================================================= */}
            {/* 3. COMERCIOS EN TENDENCIA                                 */}
            {/* ========================================================= */}
            <Container maxWidth="xl" sx={{ mt: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4, px: 1 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: '-0.5px', color: '#1a1a1a' }}>Destacados esta semana</Typography>
                        <Typography variant="subtitle1" color="text.secondary">Los lugares de los que todos hablan.</Typography>
                    </Box>
                    <Button variant="text" endIcon={<FontAwesomeIcon icon={faArrowRight}/>} sx={{ textTransform: 'none', fontWeight: 'bold', display: { xs: 'none', sm: 'flex' } }}>
                        Ver todos
                    </Button>
                </Box>

                <Grid container spacing={3}>
                    {
                        merchants.length !== 0 &&
                        merchants.map((merchant) => (
                            <Grid item xs={12} sm={6} md={4} key={merchant.id}>
                                <motion.div whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 300 }}>
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            borderRadius: 4, overflow: 'hidden', border: '1px solid #eaeaea', 
                                            cursor: 'pointer', height: '100%', bgcolor: '#fff' 
                                        }}
                                        onClick={() => redirect(`/merchants/${merchant.uuid}/profile`)}
                                    >
                                        <Box sx={{ height: 200, backgroundImage: `url(${merchant.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                                            <Chip label={merchant.category} size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 'bold' }} />
                                        </Box>
                                        <Box sx={{ p: 2.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{merchant.name}</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(245, 179, 1, 0.1)', px: 1, py: 0.5, borderRadius: 2 }}>
                                                    <FontAwesomeIcon icon={faStar} color="#f5b301" size="xs" />
                                                    <Typography variant="caption" fontWeight="bold" sx={{ ml: 0.5, color: '#d49b00' }}>{merchant.rating}</Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <FontAwesomeIcon icon={faMapMarkerAlt} /> {merchant.city}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))
                    }
                </Grid>
            </Container>

            {/* ========================================================= */}
            {/* 4. PRÓXIMOS EVENTOS                                       */}
            {/* ========================================================= */}
            <Container maxWidth="xl" sx={{ mt: 10 }}>
                <Box sx={{ mb: 4, px: 1 }}>
                    <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: '-0.5px', color: '#1a1a1a' }}>¿Qué hacer en la ciudad?</Typography>
                    <Typography variant="subtitle1" color="text.secondary">Reserva tus entradas para los mejores eventos.</Typography>
                </Box>

                <Grid container spacing={3}>
                    {
                        events.length !== 0 &&
                        events.map((event) => (
                            <Grid item xs={12} md={6} key={event.id}>
                                <motion.div whileHover={{ y: -5 }} transition={{ type: 'spring', stiffness: 300 }}>
                                    <Paper
                                        elevation={0}
                                        sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, borderRadius: 4, overflow: 'hidden', border: '1px solid #eaeaea', cursor: 'pointer', bgcolor: '#fff' }}
                                        onClick={()=>redirect(`eventos/${event.id}`)}
                                    >
                                        <Box sx={{ width: { xs: '100%', sm: '40%' }, height: { xs: 180, sm: 'auto' }, backgroundImage: `url(${event.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: { xs: '100%', sm: '60%' } }}>
                                            <Chip label={event.category} size="small" sx={{ alignSelf: 'flex-start', mb: 1.5, fontWeight: 'bold', bgcolor: 'rgba(0, 118, 182, 0.1)', color: '#0076B6' }} />
                                            <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, lineHeight: 1.2 }}>{event.name}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}><FontAwesomeIcon icon={faCalendarAlt} /> {event.start_date} • {event.start_time}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}><FontAwesomeIcon icon={faMapMarkerAlt} /> {event.city}</Typography>
                                        </Box>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))
                    }
                </Grid>
            </Container>

            {/* ========================================================= */}
            {/* 5. SECCIÓN DE CONVERSIÓN (CTA)                            */}
            {/* ========================================================= */}
            <Container maxWidth="xl" sx={{ mt: 10, mb: 5 }}>
                <Box sx={{ 
                    bgcolor: 'primary.main', 
                    borderRadius: 6, 
                    p: { xs: 4, md: 8 }, 
                    textAlign: 'center',
                    backgroundImage: 'radial-gradient(circle at top right, rgba(255,255,255,0.1), transparent)',
                    color: '#fff'
                }}>
                    <Typography variant="h3" fontWeight="900" sx={{ mb: 2, letterSpacing: '-1px' }}>Impulsa tu negocio con ATE</Typography>
                    <Typography variant="h6" sx={{ mb: 4, fontWeight: '400', maxWidth: 600, mx: 'auto', opacity: 0.9 }}>
                        Ya sea que busques descubrir nuevos lugares o quieras digitalizar y potenciar las ventas de tu comercio.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <Button 
                            variant="contained" 
                            size="large" 
                            sx={{ bgcolor: '#fff', color: 'primary.main', fontWeight: 'bold', borderRadius: 8, px: 4, py: 1.5, '&:hover': { bgcolor: '#f0f0f0' } }} 
                            onClick={() => redirect('auth/userType')}
                        >
                            Crear cuenta gratis
                        </Button>
                        <Button 
                            variant="outlined" 
                            size="large" 
                            sx={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff', fontWeight: 'bold', borderRadius: 8, px: 4, py: 1.5, '&:hover': { borderColor: '#fff' } }}
                            onClick={()=>redirect(`auth/merchantSignUp`)}
                        >
                            Soy un comercio
                        </Button>
                    </Stack>
                </Box>
            </Container>
            
        </Box>
    );
}